/**
 * ResponsiveDashboardGrid — priority-ordered dashboard container.
 *
 * On phone we stack tiles in explicit priority order — critical items
 * first, decorative charts last — so the manager immediately sees what
 * needs attention. On tablet/desktop we fall back to the caller's
 * existing grid.
 *
 * Callers wrap each tile with <ResponsiveDashboardGrid.Item priority="critical">…</Item>
 * and the container sorts them on mobile only. Desktop rendering order
 * is preserved via `desktopOrder` (default: original DOM order).
 */
import React, { Children, isValidElement } from "react";
import { cn } from "@/lib/utils";

const PRIORITY_RANK = {
  critical: 0,   // safety alerts, missed doses, safeguarding
  action:   1,   // things the user needs to do (assignments, actions)
  primary:  2,   // KPIs / today
  secondary: 3,  // supporting tiles
  chart:    4,   // decorative / trend charts
};

function Item({ children }) {
  // Item is just a semantic wrapper — Grid reads its props via cloneElement.
  return <>{children}</>;
}
Item.displayName = "ResponsiveDashboardGrid.Item";

function ResponsiveDashboardGridRoot({
  children,
  className,
  desktopClassName = "md:space-y-6",
  testid = "responsive-dashboard-grid",
  reorderOnMobile = true,
}) {
  const rawItems = Children.toArray(children).filter(isValidElement);
  // Only Items participate in reordering; anything else passes through.
  const items = rawItems.map((child, idx) => {
    const isItem = child.type?.displayName === "ResponsiveDashboardGrid.Item";
    const priority = isItem ? (child.props.priority || "secondary") : "secondary";
    return { child, idx, priority, isItem };
  });
  const desktopOrder = items;
  const mobileOrder = reorderOnMobile
    ? [...items].sort((a, b) => {
        const ra = PRIORITY_RANK[a.priority] ?? 99;
        const rb = PRIORITY_RANK[b.priority] ?? 99;
        return ra - rb || a.idx - b.idx;
      })
    : items;

  return (
    <div
      className={cn("w-full", className)}
      data-testid={testid}
    >
      {/* Phone: reordered stack */}
      <div className={cn("md:hidden space-y-4")} data-testid={`${testid}-mobile`}>
        {mobileOrder.map(({ child, idx }) => (
          <div key={`m-${idx}`} data-priority={items[idx].priority}>
            {child}
          </div>
        ))}
      </div>
      {/* Tablet/desktop: natural order + caller's spacing */}
      <div className={cn("hidden md:block", desktopClassName)} data-testid={`${testid}-desktop`}>
        {desktopOrder.map(({ child, idx }) => (
          <div key={`d-${idx}`}>{child}</div>
        ))}
      </div>
    </div>
  );
}

ResponsiveDashboardGridRoot.Item = Item;

export default ResponsiveDashboardGridRoot;
